

import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLoader } from '../../../../context/LoaderProvider.jsx';
import {
    getVendorDetails,
    updateVendorDetails,
    getAllEnquiries,
    approveVendor,
    verifyVendorStatus
} from "../../../Servicefile/clientManagementService.jsx";

// Add this helper function at the top (outside component)
const normalizeImageUrl = (url) => {
    if (!url || url === "N/A" || typeof url !== 'string') return null;

    // If it's already a full URL, return as is
    if (url.startsWith('http')) {
        return url;
    }

    // Remove leading slash if present and prepend base URL
    const cleanUrl = url.replace(/^\//, '');
    // Use the correct image server URL brandName
    return `http://img.maamaas.com:8080/${cleanUrl}`;
};

const getDefaultClientDetailsRows = () => [
    { key: "Owner Name", value: "", isUpload: false },
    { key: "Business Plan", value: "", isUpload: false },
    { key: "Position", value: "", isUpload: false },
    { key: "Branding", value: "", isUpload: false },
    { key: "Company Name", value: "", isUpload: false },
    { key: "Register Documents", value: "", isUpload: true },
    { key: "Phone No", value: "", isUpload: false },
    { key: "Email ID", value: "", isUpload: false },
    {
        key: "Bank Details",
        value: {
            holderName: "",
            branchName: "",
            accountNumber: "",
            ifscCode: "",
        },
        isUpload: false,
    },
    { key: "Passbook", value: "", isUpload: true },
    { key: "AADHAR CARD NO", value: "", isUpload: false },
    { key: "PAN CARD NO", value: "", isUpload: false },
    { key: "Website", value: "", isUpload: false },
    { key: "GST No.", value: "", isUpload: false },
];

const STATUS_FIELD_MAP = {
    "Owner Name": "ownerNameStatus",
    "Business Plan": "businessPlanStatus",
    "Company Name": "registeredNameStatus",
    "Register Documents": "registeredDocumentsStatus",
    "Phone No": "mobileNumberStatus",
    "Email ID": "emailStatus",
    "AADHAR CARD NO": "aadharNumberStatus",
    "PAN CARD NO": "panCardStatus",
    "Website": "websiteNameStatus",
    "GST No.": "gstNumberStatus",
    "Trade License": "tradeLicenseStatus",
    "FSSI License": "fssaiLicenseStatus",
    "Labour License": "labourLicenseStatus",
    "Bank Details": "blankChequeStatus",
    "Passbook": "passbookStatus",
    "Address": "addressStatus",
    "Order Type": "orderTypeStatus",
    "Business Vertical": "businessVerticalStatus",
    "Rating": "ratingStatus",
    "Trade License Number": "tradeLicenseStatus",
    "FSSAI License Number": "fssaiLicenseStatus",
    "Labour License Number": "labourLicenseStatus",
};

// Order Type options
const orderTypeOptions = [
    "DINE_IN",
    "TAKEAWAY",
    "DELIVERY",
    "TABLE_DINE_IN"
];

// Business Plan options
const businessPlanOptions = [
    "HOTEL",
    "RESTAURANT",
    "CAFE",
    "CLOUD_KITCHEN",
    "FOOD_COURT",
    "STREET_FOOD",
    "BAKERY"
];

// Business Vertical options
const businessVerticalOptions = [
    "FOOD_AND_BEVERAGES",
    "CATERINGS_SERVICES",
    "LOGISTICS_SUPPLY",
    "FRESH_GROCERIES"
];

// Rating options
const ratingOptions = [
    { value: 0, label: "0 - Not Rated" },
    { value: 1, label: "1 ★" },
    { value: 1.5, label: "1.5 ★" },
    { value: 2, label: "2 ★" },
    { value: 2.5, label: "2.5 ★" },
    { value: 3, label: "3 ★" },
    { value: 3.5, label: "3.5 ★" },
    { value: 4, label: "4 ★" },
    { value: 4.5, label: "4.5 ★" },
    { value: 5, label: "5 ★" }
];

// Safe date conversion utility
const safeDateToISOString = (dateString) => {
    if (!dateString || dateString === "N/A" || dateString === "Invalid Date") return "";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    return date.toISOString().split("T")[0];
};

// Safe date display utility
const safeDateDisplay = (dateString) => {
    if (!dateString || dateString === "N/A" || dateString === "Invalid Date") return "N/A";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";

    return date.toLocaleDateString();
};

// Updated hasValidData function
const hasValidData = (row, vendorDetails) => {
    if (!row) return false;

    const field = row.key;
    const value = row.value;

    // Special case for empty arrays
    if (Array.isArray(value)) {
        return value.length > 0 && value[0] !== "N/A";
    }

    // Check if the value exists and is not empty/"N/A"
    if (typeof value === 'string') {
        return value && value !== "N/A" && value.trim() !== "";
    }

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // For Address, Bank Details, License objects
        if (field === "Address" || field === "Bank Details" ||
            field === "Trade License" || field === "FSSI License" || field === "Labour License") {
            // Check if any property has valid data
            return Object.values(value).some(val =>
                val && val !== "N/A" && val.toString().trim() !== ""
            );
        }
        // For other objects, just check if it's not empty
        return Object.keys(value).length > 0;
    }

    // For numbers (like rating)
    if (typeof value === 'number') {
        return value !== undefined && value !== null;
    }

    // For fields that are just text labels (like "Registered Documents", "Passbook Image")
    if (value === "Registered Documents" || value === "Passbook Image") {
        // Check if there are images for these fields
        const hasImages = row.images && row.images.length > 0;
        return hasImages;
    }

    return false;
};

// Updated isFieldVerified function
const isFieldVerified = (rowKey, rowValue, vendorDetails, rowImages) => {
    const normalisedKey = rowKey.trim().replace(/\.$/, "");
    const statusField = STATUS_FIELD_MAP[normalisedKey];

    // If there's a status field in the backend, check its value
    if (statusField && vendorDetails && vendorDetails[statusField] !== undefined) {
        return vendorDetails[statusField] === true;
    }

    // Create a row object to pass to hasValidData all the required data has been not changed in the real time coding
    const rowForValidation = { key: rowKey, value: rowValue, images: rowImages };

    // Always auto-verify these specific fields if they have data various functinalities of the code also 
    const alwaysAutoVerifyFields = [
        "Business Vertical",
        "Order Type",
        "Address",
        "Rating",
        "Business Plan",
        "Position",
        "BrandName",
        "Website",
        "Phone No",
        "Email ID",
        "GST No.",
        "Trade License Number",
        "FSSAI License Number",
        "Labour License Number",
        "Owner Name",
        "Company Name",
        "AADHAR CARD NO",
        "PAN CARD NO"
    ];

    if (alwaysAutoVerifyFields.includes(rowKey)) {
        return hasValidData(rowForValidation, vendorDetails);
    }

    // For document/image fields, check both data and images
    const documentFields = [
        "Register Documents",
        "Trade License",
        "FSSI License",
        "Labour License",
        "Bank Details",
        "Passbook"
    ];

    if (documentFields.includes(rowKey)) {
        // For document fields, require images
        const hasImages = rowImages && rowImages.length > 0;
        return hasImages;
    }

    return false;
};

const isFieldRejected = (rowKey, vendorDetails) => {
    const normalisedKey = rowKey.trim().replace(/\.$/, "");
    const statusField = STATUS_FIELD_MAP[normalisedKey];

    if (!statusField || !vendorDetails) return false;

    return vendorDetails[statusField] === false;
};

// New function to check if a field can be verified
const canVerifyField = (rowKey, rowValue, vendorDetails, rowImages) => {
    // Check if there's valid data
    return hasValidData({ key: rowKey, value: rowValue, images: rowImages }, vendorDetails);
};

/* ---------------- component ---------------- */
const FoodPaidClents = () => {
    /* -------- list‑screen state -------- */
    const [rolesData, setRolesData] = useState([]);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const { showLoader, hideLoader } = useLoader();

    // Filter states
    const [searchType, setSearchType] = useState("vendorId");
    const [searchQuery, setSearchQuery] = useState("");
    const [cityFilter, setCityFilter] = useState("");
    const [businessVerticalFilter, setBusinessVerticalFilter] = useState("");
    const [filteredData, setFilteredData] = useState([]);

    /* -------- detail‑screen state -------- */
    const [selectedClient, setSelectedClient] = useState(null);
    const [vendorDetails, setVendorDetails] = useState(null);
    const [vendorNotFound, setVendorNotFound] = useState(false);
    const [vendorError, setVendorError] = useState(null);
    const [loading, setLoading] = useState(false);

    const [rows, setRows] = useState(getDefaultClientDetailsRows());
    const [verifiedRows, setVerifiedRows] = useState([]);
    const [rejectedRows, setRejectedRows] = useState([]);
    const [isApproved, setIsApproved] = useState(false);
    const [imageLoadErrors, setImageLoadErrors] = useState(new Set());

    const fileInputRef = useRef(null);
    const [vendorData, setVendorData] = useState({});
    const [rowId, setRowId] = useState(null);

    // Screen resize effect
    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const getCurrentVendorId = () => {
        return (
            selectedClient?.vendorId ||
            selectedClient?.vendorEnquiry?.vendorId ||
            vendorDetails?.vendorId ||
            null
        );
    };
    const handleApproveAll = async () => {
        try {
            const token = localStorage.getItem("authToken");
            const vendorId = getCurrentVendorId();

            if (!vendorId) return alert("Vendor ID not found ❌");

            const body = {
                approvalStatus: true,
                approvalRemarks: "Approved by admin"
            };

            const data = await approveVendor(vendorId, body, token);

            console.log("API Response:", data);

            alert("Vendor Approved Successfully ✅");

            fetchVendorDetails(vendorId);

        } catch (error) {
            console.error("Approval Error:", error);
            alert("Approval Failed ❌");
        }
    };
    const vendorId = getCurrentVendorId();

    const handleImageError = (imageUrl, fieldName, index) => {
        const errorKey = `${fieldName}-${index}`;
        setImageLoadErrors(prev => new Set([...prev, errorKey]));
    };

    const handleImageLoad = (imageUrl, fieldName, index) => {
        const errorKey = `${fieldName}-${index}`;
        setImageLoadErrors(prev => {
            const newSet = new Set(prev);
            newSet.delete(errorKey);
            return newSet;
        });
    };

    const isImageFailed = (fieldName, index) => {
        return imageLoadErrors.has(`${fieldName}-${index}`);
    };

    const fetchVendorDetails =
        async (vendorId) => {

            if (!vendorId) return;

            setLoading(true);

            setVendorNotFound(false);

            setVendorError(null);

            const token =
                localStorage.getItem(
                    "authToken"
                );

            try {

                // 🔥 SERVICE API CALL
                const data =
                    await getVendorDetails(
                        vendorId,
                        token
                    );

                setVendorDetails(data);

                setVendorNotFound(false);

                setVendorError(null);

            } catch (err) {

                console.error(
                    "❌ get-vendor-details error:",
                    err
                );

                // 🔥 HANDLE VENDOR NOT FOUND
                if (
                    err.response?.status === 500 &&
                    err.response?.data?.message ===
                    "vendorId not found"
                ) {

                    setVendorNotFound(true);

                    setVendorDetails(null);

                    setVendorError(
                        "Vendor not registered"
                    );

                } else {

                    setVendorError(
                        "Failed to fetch vendor details"
                    );
                }

            } finally {

                setLoading(false);
            }
        };

    const formatDate = (date) => {
        if (!date) return "—";

        const d = new Date(date);

        if (isNaN(d.getTime())) return "—";

        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();

        return `${day}-${month}-${year}`;
    };

    const handleSaveRow = async (rowIndex) => {
        const token =
            localStorage.getItem("authToken") ||
            localStorage.getItem("token");

        if (!token) {
            alert("No authentication token found");
            return;
        }

        try {
            const vendorId = getCurrentVendorId();

            if (!vendorId) {
                alert("Vendor ID not found");
                return;
            }

            const row = rows[rowIndex];

            const formData = new FormData();
            const vendorData = {};

            const fieldMapping = {
                "Owner Name": "ownerName",
                "Phone No": "mobileNumber",
                "Position": "position",
                "Branding": "brandName",
                "Website": "websiteName",
                "Email ID": "email",
                "Company Name": "registeredName",
                "AADHAR CARD NO": "aadharNumber",
                "PAN CARD NO": "panCardNumber",
                "GST No.": "gstNumber",
                "Order Type": "orderTypes",
                "Business Plan": "businessPlan",
                "Trade License Number": "tradeLicenseNumber",
                "FSSAI License Number": "fssaiLicenseNumber",
                "Labour License Number": "labourLicenseNumber",
                "Rating": "rating"
            };

            const backendFieldName = fieldMapping[row.key];

            // Basic Fields
            if (backendFieldName && row.value) {

                if (row.key === "Order Type") {
                    vendorData[backendFieldName] = Array.isArray(row.value)
                        ? row.value
                        : [row.value];
                }
                else if (row.key === "Rating") {
                    vendorData[backendFieldName] =
                        parseFloat(row.value) || 0;
                }
                else if (row.value !== "N/A") {
                    vendorData[backendFieldName] = row.value;
                }
            }

            // Address
            if (
                row.key === "Address" &&
                typeof row.value === "object"
            ) {
                vendorData.doorNumber = row.value.doorNo || "";
                vendorData.addressLine = row.value.street || "";
                vendorData.city = row.value.city || "";
                vendorData.state = row.value.state || "";
                vendorData.pincode = row.value.pincode || "";
                vendorData.landMark = row.value.landmark || "";
                vendorData.latitude =
                    row.value.latitude
                        ? parseFloat(row.value.latitude)
                        : null;
                vendorData.longitude =
                    row.value.longitude
                        ? parseFloat(row.value.longitude)
                        : null;
            }

            // Bank Details
            if (
                row.key === "Bank Details" &&
                typeof row.value === "object"
            ) {
                vendorData.holderName =
                    row.value.holderName || "";

                vendorData.branchName =
                    row.value.branchName || "";

                vendorData.accountNumber =
                    row.value.accountNumber || "";

                vendorData.ifscCode =
                    row.value.ifscCode || "";
            }

            // Trade License
            if (
                row.key === "Trade License" &&
                typeof row.value === "object"
            ) {
                vendorData.tradeLicenseStartDate =
                    row.value.startDate || "";

                vendorData.tradeLicenseEndDate =
                    row.value.endDate || "";
            }

            // FSSAI License
            if (
                row.key === "FSSI License" &&
                typeof row.value === "object"
            ) {
                vendorData.fssaiStartDate =
                    row.value.startDate || "";

                vendorData.fssaiEndDate =
                    row.value.endDate || "";
            }

            // Labour License
            if (
                row.key === "Labour License" &&
                typeof row.value === "object"
            ) {
                vendorData.labourStartDate =
                    row.value.startDate || "";

                vendorData.labourEndDate =
                    row.value.endDate || "";
            }

            // Append JSON
            formData.append(
                "vendorData",
                new Blob(
                    [JSON.stringify(vendorData)],
                    {
                        type: "application/json"
                    }
                )
            );

            // Append Images
            if (row.images?.length > 0) {
                row.images.forEach((img, idx) => {

                    if (img.file) {

                        const imageFieldName =
                            getImageFieldName(
                                row.key,
                                idx
                            );

                        formData.append(
                            imageFieldName,
                            img.file
                        );
                    }
                });
            }

            // ==========================
            // DEBUG LOGS
            // ==========================

            console.log("Vendor ID:", vendorId);

            console.log(
                "Vendor Data JSON:",
                vendorData
            );

            for (let pair of formData.entries()) {
                console.log(
                    "FORM DATA =>",
                    pair[0],
                    pair[1]
                );
            }

            // API CALL
            const response =
                await updateVendorDetails(
                    vendorId,
                    formData,
                    token
                );

            console.log(
                "Update Response:",
                response
            );

            alert(
                "Data updated successfully!"
            );

            setRows((prev) =>
                prev.map((r, idx) =>
                    idx === rowIndex
                        ? {
                            ...r,
                            isEditing: false
                        }
                        : r
                )
            );

            await fetchVendorDetails(vendorId);

        } catch (err) {

            console.error(
                "Full Update Error:",
                err
            );

            console.log(
                "Status:",
                err.response?.status
            );

            console.log(
                "Response Data:",
                err.response?.data
            );

            console.log(
                "Response Headers:",
                err.response?.headers
            );

            alert(
                err.response?.data?.message ||
                err.message ||
                "Failed to update vendor"
            );
        }
    };

    const getImageFieldName = (rowKey, imageIndex) => {
        const fieldMap = {
            "Register Documents": imageIndex === 0 ? "registeredDocumentsFront" : "registeredDocumentsBack",
            "AADHAR CARD NO": imageIndex === 0 ? "aadharPhotoFront" : "aadharPhotoBack",
            "PAN CARD NO": "panCard",
            "Trade License": "tradeLicense",
            "FSSI License": "fssaiLicense",
            "Labour License": "labourLicense",
            "Bank Details": "blankCheque",
            "Passbook": "passbook",
            "Company Name": "companyLogo",
        };

        return fieldMap[rowKey] || `image${imageIndex + 1}`;
    };

    const handleVerifyy =
        async (i) => {

            const normalisedKey =
                rows[i].key
                    .trim()
                    .replace(/\.$/, "");

            const statusField =
                STATUS_FIELD_MAP[
                normalisedKey
                ];

            if (!statusField) {

                console.warn(
                    "No status mapping for:",
                    rows[i].key
                );

                return;
            }

            const vendorId =
                getCurrentVendorId();

            if (!vendorId) {

                alert(
                    "Vendor ID is missing"
                );

                return;
            }

            // VENDOR NOT FOUND
            if (vendorNotFound) {

                alert(
                    "Vendor not registered. Cannot verify."
                );

                return;
            }

            const token =
                localStorage.getItem(
                    "authToken"
                );

            if (!token) {

                alert(
                    "Authorization token missing"
                );

                return;
            }

            try {

                // ====================================
                // CREATE VENDOR DATA
                // ====================================

                const vendorData = {

                    ownerNameStatus:
                        vendorDetails
                            ?.ownerNameStatus ??
                        false,

                    businessPlanStatus:
                        vendorDetails
                            ?.businessPlanStatus ??
                        false,

                    registeredNameStatus:
                        vendorDetails
                            ?.registeredNameStatus ??
                        false,

                    registeredDocumentsStatus:
                        vendorDetails
                            ?.registeredDocumentsStatus ??
                        false,

                    mobileNumberStatus:
                        vendorDetails
                            ?.mobileNumberStatus ??
                        false,

                    emailStatus:
                        vendorDetails
                            ?.emailStatus ??
                        false,

                    aadharNumberStatus:
                        vendorDetails
                            ?.aadharNumberStatus ??
                        false,

                    panCardStatus:
                        vendorDetails
                            ?.panCardStatus ??
                        false,

                    websiteNameStatus:
                        vendorDetails
                            ?.websiteNameStatus ??
                        false,

                    gstNumberStatus:
                        vendorDetails
                            ?.gstNumberStatus ??
                        false,

                    tradeLicenseStatus:
                        vendorDetails
                            ?.tradeLicenseStatus ??
                        false,

                    fssaiLicenseStatus:
                        vendorDetails
                            ?.fssaiLicenseStatus ??
                        false,

                    labourLicenseStatus:
                        vendorDetails
                            ?.labourLicenseStatus ??
                        false,

                    blankChequeStatus:
                        vendorDetails
                            ?.blankChequeStatus ??
                        false,

                    passbookStatus:
                        vendorDetails
                            ?.passbookStatus ??
                        false,

                    addressStatus:
                        vendorDetails
                            ?.addressStatus ??
                        false,

                    orderTypeStatus:
                        vendorDetails
                            ?.orderTypeStatus ??
                        false,

                    businessVerticalStatus:
                        vendorDetails
                            ?.businessVerticalStatus ??
                        false,

                    ratingStatus:
                        vendorDetails
                            ?.ratingStatus ??
                        false,

                    // UPDATE CURRENT STATUS
                    [statusField]: true,
                };

                // ====================================
                // UPDATE UI IMMEDIATELY
                // ====================================

                setVendorDetails(
                    (prev) => ({
                        ...prev,

                        [statusField]:
                            true,
                    })
                );

                // ====================================
                // 🔥 SERVICE API CALL
                // ====================================

                await verifyVendorStatus(
                    vendorId,
                    vendorData,
                    token
                );

                // REFRESH DATA
                await fetchVendorDetails(
                    vendorId
                );

                alert(
                    "Vendor data verified successfully!"
                );

            } catch (err) {

                console.error(
                    "❌ Verify error:",
                    err
                );

                // REVERT UI
                setVendorDetails(
                    (prev) => ({
                        ...prev,

                        [statusField]:
                            false,
                    })
                );

                if (
                    err.response?.status ===
                    500 &&
                    err.response?.data
                        ?.message ===
                    "vendorId not found"
                ) {

                    alert(
                        "Vendor not registered. Cannot verify."
                    );

                } else if (
                    err.response?.status ===
                    404
                ) {

                    alert(
                        "Vendor not registered. Cannot verify."
                    );

                } else {

                    alert(
                        `Failed to update status: ${err.response?.data
                            ?.message ||
                        err.message
                        }`
                    );
                }
            }
        };

    const handleReject =
        async (i) => {

            const normalisedKey =
                rows[i].key
                    .trim()
                    .replace(/\.$/, "");

            const statusField =
                STATUS_FIELD_MAP[
                normalisedKey
                ];

            if (!statusField) {

                console.warn(
                    "No status mapping for:",
                    rows[i].key
                );

                return;
            }

            const vendorId =
                getCurrentVendorId();

            if (!vendorId) {

                alert(
                    "Vendor ID is missing – cannot update."
                );

                return;
            }

            // VENDOR NOT FOUND
            if (vendorNotFound) {

                alert(
                    "Vendor not registered. Cannot reject."
                );

                return;
            }

            const token =
                localStorage.getItem(
                    "authToken"
                );

            if (!token) {

                alert(
                    "Authorization token missing. Please log in."
                );

                return;
            }

            try {

                // ====================================
                // CREATE VENDOR DATA
                // ====================================

                const vendorData = {

                    ownerNameStatus:
                        vendorDetails
                            ?.ownerNameStatus ??
                        false,

                    businessPlanStatus:
                        vendorDetails
                            ?.businessPlanStatus ??
                        false,

                    registeredNameStatus:
                        vendorDetails
                            ?.registeredNameStatus ??
                        false,

                    registeredDocumentsStatus:
                        vendorDetails
                            ?.registeredDocumentsStatus ??
                        false,

                    mobileNumberStatus:
                        vendorDetails
                            ?.mobileNumberStatus ??
                        false,

                    emailStatus:
                        vendorDetails
                            ?.emailStatus ??
                        false,

                    aadharNumberStatus:
                        vendorDetails
                            ?.aadharNumberStatus ??
                        false,

                    panCardStatus:
                        vendorDetails
                            ?.panCardStatus ??
                        false,

                    websiteNameStatus:
                        vendorDetails
                            ?.websiteNameStatus ??
                        false,

                    gstNumberStatus:
                        vendorDetails
                            ?.gstNumberStatus ??
                        false,

                    tradeLicenseStatus:
                        vendorDetails
                            ?.tradeLicenseStatus ??
                        false,

                    fssaiLicenseStatus:
                        vendorDetails
                            ?.fssaiLicenseStatus ??
                        false,

                    labourLicenseStatus:
                        vendorDetails
                            ?.labourLicenseStatus ??
                        false,

                    blankChequeStatus:
                        vendorDetails
                            ?.blankChequeStatus ??
                        false,

                    passbookStatus:
                        vendorDetails
                            ?.passbookStatus ??
                        false,

                    addressStatus:
                        vendorDetails
                            ?.addressStatus ??
                        false,

                    orderTypeStatus:
                        vendorDetails
                            ?.orderTypeStatus ??
                        false,

                    businessVerticalStatus:
                        vendorDetails
                            ?.businessVerticalStatus ??
                        false,

                    ratingStatus:
                        vendorDetails
                            ?.ratingStatus ??
                        false,

                    // 🔥 REJECT STATUS
                    [statusField]: false,
                };

                // ====================================
                // UPDATE UI IMMEDIATELY
                // ====================================

                setVendorDetails(
                    (prev) => ({
                        ...prev,

                        [statusField]:
                            false,
                    })
                );

                // ====================================
                // 🔥 SERVICE API CALL
                // ====================================

                await verifyVendorStatus(
                    vendorId,
                    vendorData,
                    token
                );

                // REFRESH DATA
                await fetchVendorDetails(
                    vendorId
                );

                alert(
                    "Vendor status rejected successfully!"
                );

            } catch (err) {

                console.error(
                    "❌ Reject error:",
                    err
                );

                // REVERT UI
                setVendorDetails(
                    (prev) => ({
                        ...prev,

                        [statusField]:
                            true,
                    })
                );

                if (
                    err.response?.status ===
                    500 &&
                    err.response?.data
                        ?.message ===
                    "vendorId not found"
                ) {

                    alert(
                        "Vendor not registered. Cannot reject."
                    );

                } else if (
                    err.response?.status ===
                    404
                ) {

                    alert(
                        "Vendor not registered. Cannot reject."
                    );

                } else {

                    alert(
                        `Failed to reject status: ${err.response?.data
                            ?.message ||
                        err.message
                        }`
                    );
                }
            }
        };

    // Fetch subscriptions from new API endpoint
    useEffect(() => {

        const token = localStorage.getItem("authToken");

        const fetchData = async () => {

            showLoader();

            try {

                const data = await getAllEnquiries(token);

                console.log("API Response:", data);

                const subscriptions = Array.isArray(data)
                    ? data
                    : [];

                // Filter FOOD_AND_BEVERAGES vendors/employees
                const filtered = subscriptions.filter(
                    (item) =>
                        item.businessVerticals &&
                        item.businessVerticals.includes("FOOD_AND_BEVERAGES") &&
                        (
                            item.parentId === null ||
                            item.parentId === 0 ||
                            item.parentId === undefined
                        )
                );

                console.log("Filtered Data:", filtered);

                setRolesData(filtered);
                setFilteredData(filtered);

                if (filtered.length > 0) {

                    const vendor = filtered[0];

                    const vendorId = vendor?.vendorId;
                    const parentId = vendor?.parentId;

                    // Only fetch when parentId is null/0
                    if (
                        vendorId &&
                        (
                            parentId === null ||
                            parentId === 0 ||
                            parentId === undefined
                        )
                    ) {
                        fetchVendorDetails(vendorId);
                    } else {
                        console.log(
                            "parentId exists, API call skipped:",
                            parentId
                        );
                    }
                }

            } catch (err) {

                console.error(
                    "Payment fetch error:",
                    err
                );

            } finally {

                hideLoader();
            }
        };

        fetchData();

    }, []);

    // Apply filters
    useEffect(() => {
        let result = rolesData;

        // Apply city filter
        if (cityFilter) {
            result = result.filter(item => item.city === cityFilter);
        }

        // Apply business vertical filter
        if (businessVerticalFilter) {
            result = result.filter(item => item.businessVertical === businessVerticalFilter);
        }

        // Apply search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            result = result.filter(item => {
                // Get the field value based on searchType
                let fieldValue;

                // Handle different field names in the subscription data
                if (searchType === "vendorId") {
                    fieldValue = item.vendorId;
                } else if (searchType === "phoneNumber") {
                    fieldValue = item.customerMobile;
                } else if (searchType === "email") {
                    fieldValue = item.email || item.customerEmail;
                } else if (searchType === "name") {
                    fieldValue = item.customerName;
                } else if (searchType === "city") {
                    fieldValue = item.city;
                } else if (searchType === "businessVertical") {
                    fieldValue = item.businessVertical;
                } else {
                    fieldValue = item[searchType];
                }

                if (!fieldValue) return false;

                // For phone number search, clean non-digits
                if (searchType === "phoneNumber") {
                    const cleanPartnerMobile = fieldValue.toString().replace(/\D/g, '');
                    const cleanQuery = query.replace(/\D/g, '');
                    return fieldValue.toString().toLowerCase().includes(query) ||
                        cleanPartnerMobile.includes(cleanQuery);
                }

                // For other fields
                return fieldValue.toString().toLowerCase().includes(query);
            });
        }

        setFilteredData(result);
    }, [rolesData, cityFilter, businessVerticalFilter, searchType, searchQuery]);

    // Get unique values for filter dropdowns
    const uniqueCities = useMemo(() => {
        const cities = rolesData.map(item => item.city).filter(Boolean);
        return [...new Set(cities)].sort();
    }, [rolesData]);

    const uniqueBusinessVerticals = useMemo(() => {
        const verticals = rolesData.map(item => item.businessVertical).filter(Boolean);
        return [...new Set(verticals)].sort();
    }, [rolesData]);

    // Clear all filters
    const clearAllFilters = () => {
        setSearchQuery("");
        setCityFilter("");
        setBusinessVerticalFilter("");
        setSearchType("vendorId");
    };

    // Get placeholder text based on search type
    const getPlaceholderText = () => {
        switch (searchType) {
            case "vendorId": return "Search Vendor ID";
            case "name": return "Search Customer Name";
            case "email": return "Search Email";
            case "phoneNumber": return "Search Mobile Number";
            case "city": return "Search City";
            case "businessVertical": return "Search Business Vertical";
            default: return "Search...";
        }
    };

    // Search options - Added businessVertical
    const searchOptions = [
        { value: "vendorId", label: "Vendor ID" },
        { value: "name", label: "Customer Name" },
        { value: "createdAt", label: "Date" },
        { value: "email", label: "Email" },
        { value: "phoneNumber", label: "Mobile Number" },
        { value: "city", label: "City" },
        { value: "businessVertical", label: "Business Vertical" }
    ];

    useEffect(() => {
        if (!selectedClient) {
            setRows(getDefaultClientDetailsRows());
            setVerifiedRows([]);
            setRejectedRows([]);
            setIsApproved(false);
            setImageLoadErrors(new Set());
            return;
        }

        const merged = { ...selectedClient, ...(vendorDetails || {}) };

        // REQUIRED Image mapping only - using normalizeImageUrl
        const imageMap = {
            "Register Documents": [
                normalizeImageUrl(merged.registeredDocumentsFront),
                normalizeImageUrl(merged.registeredDocumentsBack)
            ].filter(url => url),
            "AADHAR CARD NO": [
                normalizeImageUrl(merged.aadharPhotoFront),
                normalizeImageUrl(merged.aadharPhotoBack)
            ].filter(url => url),
            "PAN CARD NO": [normalizeImageUrl(merged.panCard)].filter(url => url),
            "Trade License": [normalizeImageUrl(merged.tradeLicense)].filter(url => url),
            "FSSI License": [normalizeImageUrl(merged.fssaiLicense)].filter(url => url),
            "Labour License": [normalizeImageUrl(merged.labourLicense)].filter(url => url),
            "Bank Details": [normalizeImageUrl(merged.blankCheque)].filter(url => url),
            "Passbook": [normalizeImageUrl(merged.passbook)].filter(url => url),
            "Company Name": [normalizeImageUrl(merged.companyLogo)].filter(url => url),
        };

        const newRows = [
            { key: "Owner Name", value: merged.ownerName || "N/A" },
            { key: "Company Name", value: merged.registeredName || "N/A" },
            { key: "Business Plan", value: merged.businessPlan || "N/A" },
            { key: "Position", value: merged.position || "N/A" },
            { key: "Branding", value: merged.brandName || "N/A" },
            { key: "Business Vertical", value: merged.businessVertical || "N/A" },

            {
                key: "Order Type",
                value: Array.isArray(merged.orderTypes)
                    ? merged.orderTypes
                    : typeof merged.orderTypes === "string" && merged.orderTypes !== "N/A"
                        ? merged.orderTypes.split(",").map(v => v.trim())
                        : [],
            },

            { key: "Website", value: merged.websiteName || "N/A" },
            { key: "Phone No", value: merged.mobileNumber || merged.customerMobile || "N/A" },
            { key: "Email ID", value: merged.email || merged.customerEmail || "N/A" },
            { key: "City", value: merged.city || "N/A" },

            {
                key: "Address",
                value: {
                    doorNo: merged.doorNumber || "N/A",
                    street: merged.addressLine || "N/A",
                    city: merged.city || "N/A",
                    state: merged.state || "N/A",
                    pincode: merged.pincode || "N/A",
                    landmark: merged.landMark || "N/A",
                    latitude: merged.latitude?.toString() || "N/A",
                    longitude: merged.longitude?.toString() || "N/A",
                },
            },

            { key: "Register Documents", value: "Registered Documents" },
            { key: "AADHAR CARD NO", value: merged.aadharNumber || "N/A" },
            { key: "PAN CARD NO", value: merged.panCardNumber || "N/A" },

            // Trade License Number
            { key: "Trade License Number", value: merged.tradeLicenseNumber || "N/A" },
            {
                key: "Trade License",
                value: {
                    startDate: merged.tradeLicenseStartDate || "N/A",
                    endDate: merged.tradeLicenseEndDate || "N/A",
                },
            },

            // FSSAI License Number
            { key: "FSSAI License Number", value: merged.fssaiLicenseNumber || "N/A" },
            {
                key: "FSSI License",
                value: {
                    startDate: merged.fssaiStartDate || "N/A",
                    endDate: merged.fssaiEndDate || "N/A",
                },
            },

            // Labour License Number
            { key: "Labour License Number", value: merged.labourLicenseNumber || "N/A" },
            {
                key: "Labour License",
                value: {
                    startDate: merged.labourStartDate || "N/A",
                    endDate: merged.labourEndDate || "N/A",
                },
            },

            { key: "GST No.", value: merged.gstNumber || "N/A" },

            {
                key: "Bank Details",
                value: {
                    holderName: merged.holderName || merged.accountHolderName || "N/A",
                    branchName: merged.branchName || merged.bankName || "N/A",
                    accountNumber: merged.accountNumber || merged.accountNo || "N/A",
                    ifscCode: merged.ifscCode || "N/A",
                },
            },

            { key: "Passbook", value: "Passbook Image" },

            // Rating field added after Passbook
            { key: "Rating", value: merged.rating || 0 },

            // Only keep Selected Modules and Remaining Days from subscription data
            { key: "Selected Modules", value: merged.selectedModules?.join(", ") || "N/A" },
            { key: "Remaining Days", value: merged.remainingDays ? `${merged.remainingDays} days` : "N/A" },
        ];

        const updatedRows = newRows.map((row) => ({
            ...row,
            images: imageMap[row.key] || [],
        }));

        setRows(updatedRows.map((r) => ({ ...r, isEditing: false })));
        setVerifiedRows([]);
        setRejectedRows([]);
        setIsApproved(false);
        setImageLoadErrors(new Set());
    }, [selectedClient, vendorDetails]);

    const handleViewClick = (client) => {
        setSelectedClient(client);
        const vendorId = client?.vendorId;
        setVendorNotFound(false);
        setVendorError(null);
        fetchVendorDetails(vendorId);
    };

    const handleBackClick = () => {
        setSelectedClient(null);
        setVendorNotFound(false);
        setVendorError(null);
    };

    const handleFileChange = (e, index) => {
        const fileName = e.target.files[0]?.name || "";
        const updated = [...rows];
        updated[index].value = fileName;
        setRows(updated);
    };

    // Clean Image Display Component with "-" for missing images
    const ImageDisplay = ({ images, fieldName }) => {
        if (!images || images.length === 0) {
            return (
                <div className="text-center">
                    <span style={{ fontSize: "18px", color: "#6c757d" }}>-</span>
                </div>
            );
        }

        return (
            <div className="d-flex justify-content-center gap-2 flex-wrap">
                {images.map((img, idx) => {
                    const imageUrl = typeof img === 'string' ? img : img.url;
                    const hasFailed = isImageFailed(fieldName, idx);

                    if (hasFailed || !imageUrl) {
                        return (
                            <div key={idx} className="text-center">
                                <span style={{ fontSize: "18px", color: "#6c757d" }}>-</span>
                            </div>
                        );
                    }

                    return (
                        <div key={idx} className="text-center position-relative">
                            <img
                                src={imageUrl}
                                alt={`${fieldName}-${idx}`}
                                style={{
                                    width: fieldName === "Company Name" ? "80px" : "60px",
                                    height: fieldName === "Company Name" ? "80px" : "60px",
                                    borderRadius: "6px",
                                    objectFit: "cover",
                                    border: "1px solid #dee2e6",
                                }}
                                onError={() => handleImageError(imageUrl, fieldName, idx)}
                                onLoad={() => handleImageLoad(imageUrl, fieldName, idx)}
                            />
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12" style={{ marginTop: "10px" }}>
                    <div className="client" style={{ overflowY: "auto", height: "450px" }}>
                        {!selectedClient ? (
                            <>
                                {/* Filter Controls */}
                                <div className="row g-2 mb-3 align-items-center me-0">
                                    {/* Search Type Dropdown and Search Input */}
                                    {/* Various sections of the particular data not */}

                                    <div className="col-md-auto col-12">
                                        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                                            {/* Search Type Dropdown */}
                                            <div style={{ minWidth: '150px' }}>
                                                <select
                                                    className="form-select"
                                                    value={searchType}
                                                    onChange={(e) => setSearchType(e.target.value)}
                                                    style={{ height: "40px" }}
                                                >
                                                    {searchOptions.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Search Input */}
                                            <div style={{ minWidth: '200px' }}>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={getPlaceholderText()}
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    style={{ height: "40px" }}
                                                />
                                            </div>
                                        </div>
                                    </div>



                                    {/* Clear Filters Button */}
                                    <div className="col-md-auto col-12">
                                        <button
                                            className="btn"
                                            onClick={clearAllFilters}
                                            style={{
                                                backgroundColor: '#6c757d',
                                                color: '#fff',
                                                height: "40px",
                                                padding: "0 15px"
                                            }}
                                        >
                                            Clear Filters
                                        </button>
                                    </div>

                                    {/* Refresh Data Button */}
                                    <div className="col-md col-12" >
                                        <div className="d-flex justify-content-start justify-content-md-end">
                                            <button
                                                className="btn"
                                                onClick={() => window.location.reload()}
                                                style={{
                                                    backgroundColor: 'rgb(230 109 51)',
                                                    color: '#fff',
                                                    height: "40px",
                                                    padding: "0 20px"
                                                }}
                                            >
                                                Refresh Data
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="sed pe-3">
                                    <table style={tableStyle} className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th style={thStyle}>S.NO</th>
                                                <th style={thStyle}>Date</th>
                                                <th style={thStyle}>Customer Name</th>
                                                <th style={thStyle}>Resturent Name</th>
                                                <th style={thStyle}>Email</th>
                                                <th style={thStyle}>Mobile</th>
                                                <th style={thStyle}>City</th>

                                                <th style={thStyle}>Vendor ID</th>
                                                <th style={thStyle}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredData.length ? (
                                                filteredData.map((c, idx) => (
                                                    <tr key={c.subscriptionId || idx}>
                                                        <td style={thTdStyle}>{idx + 1}</td>
                                                        <td style={thTdStyle}>
                                                            {formatDate(c?.registerTime)}
                                                        </td>
                                                        <td style={thTdStyle}>{c?.name || "—"}</td>
                                                        {/* Restaurant Name */}
                                                        <td style={thTdStyle}>
                                                            {c?.registeredName || c?.companyName || "—"}
                                                        </td>
                                                        <td style={thTdStyle}>{c?.email || c?.customerEmail || "—"}</td>
                                                        <td style={thTdStyle}>{c?.mobileNumber || "—"}</td>
                                                        <td style={thTdStyle}>{c?.city || "—"}</td>

                                                        <td style={thTdStyle}>{c?.vendorId || "—"}</td>
                                                        <td style={thTdStyle}>
                                                            <button
                                                                style={{ ...buttonStyle, color: "rgb(230 109 51)", fontWeight: "bold" }}
                                                                onClick={() => handleViewClick(c)}
                                                            >
                                                                View
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td style={thTdStyle} colSpan={8}>
                                                        {rolesData.length === 0
                                                            ? "Loading data or no customers found."
                                                            : (searchQuery.trim() || cityFilter || businessVerticalFilter)
                                                                ? "No data found with the specified filters."
                                                                : "No data available."}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <div className="mt-4 rounded-3 shadow-sm">
                                <div className="d-flex align-items-center mb-4">
                                    <button
                                        onClick={handleBackClick}
                                        className="btn fw-bold px-4 py-2"
                                        style={{ backgroundColor: "rgb(230 109 51)", color: "#fff" }}
                                    >
                                        Back to List
                                    </button>
                                </div>

                                {loading && (
                                    <div className="text-center my-4">
                                        <span className="spinner-border text-primary" />
                                    </div>
                                )}

                                {!loading && vendorNotFound && (
                                    <div className="text-center my-5 py-5">
                                        <div style={{ fontSize: "24px", color: "#dc3545", marginBottom: "16px" }}>
                                            ⚠️ Vendor Not Registered
                                        </div>
                                        <p style={{ fontSize: "16px", color: "#6c757d" }}>
                                            This vendor has a subscription but is not registered in the system.
                                            <br />
                                            Vendor ID: {selectedClient?.vendorId}
                                        </p>
                                    </div>
                                )}

                                {!loading && !vendorNotFound && vendorDetails && (
                                    <div className="register mt-4">
                                        <div className="registertable bo2">
                                            <table className="table table-bordered table-striped align-middle mt-4 w-100">
                                                <thead>
                                                    <tr>
                                                        <th style={detailThStyle}>Field</th>
                                                        <th style={detailThStyle}>Value</th>
                                                        <th style={detailThStyle}>Image</th>
                                                        <th style={detailThStyle}>Status</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {rows.map((r, i) => {
                                                        const isEditing = r.isEditing || false;

                                                        const handleInputChange = (field, value) => {
                                                            setRows((prevRows) =>
                                                                prevRows.map((row, idx) =>
                                                                    idx === i
                                                                        ? {
                                                                            ...row,
                                                                            value:
                                                                                typeof row.value === "object"
                                                                                    ? { ...row.value, [field]: value }
                                                                                    : value,
                                                                        }
                                                                        : row
                                                                )
                                                            );
                                                        };

                                                        // Handle Order Type selection (multiple)
                                                        const handleOrderTypeChange = (selectedValue) => {
                                                            setRows((prevRows) =>
                                                                prevRows.map((row, idx) => {
                                                                    if (idx === i) {
                                                                        const currentValues = Array.isArray(row.value) ? row.value : [];
                                                                        const isSelected = currentValues.includes(selectedValue);

                                                                        if (isSelected) {
                                                                            // Remove if already selected
                                                                            return {
                                                                                ...row,
                                                                                value: currentValues.filter(v => v !== selectedValue)
                                                                            };
                                                                        } else {
                                                                            // Add if not selected
                                                                            return {
                                                                                ...row,
                                                                                value: [...currentValues, selectedValue]
                                                                            };
                                                                        }
                                                                    }
                                                                    return row;
                                                                })
                                                            );
                                                        };

                                                        // Handle Business Plan selection
                                                        const handleBusinessPlanChange = (selectedValue) => {
                                                            setRows((prevRows) =>
                                                                prevRows.map((row, idx) =>
                                                                    idx === i
                                                                        ? {
                                                                            ...row,
                                                                            value: selectedValue,
                                                                        }
                                                                        : row
                                                                )
                                                            );
                                                        };

                                                        // Handle Rating selection
                                                        const handleRatingChange = (selectedValue) => {
                                                            setRows((prevRows) =>
                                                                prevRows.map((row, idx) =>
                                                                    idx === i
                                                                        ? {
                                                                            ...row,
                                                                            value: parseFloat(selectedValue),
                                                                        }
                                                                        : row
                                                                )
                                                            );
                                                        };

                                                        const handleImageChange = (e) => {
                                                            const files = Array.from(e.target.files);
                                                            const newImages = files.map((file) => ({
                                                                url: URL.createObjectURL(file),
                                                                file
                                                            }));

                                                            setRows(prev =>
                                                                prev.map((row, idx) =>
                                                                    idx === i
                                                                        ? {
                                                                            ...row,
                                                                            images: [...(row.images || []), ...newImages]
                                                                        }
                                                                        : row
                                                                )
                                                            );
                                                        };

                                                        // Check if field is editable
                                                        const isEditableField = ![
                                                            "Selected Modules",
                                                            "Remaining Days",
                                                            "Business Vertical",
                                                            "City"
                                                        ].includes(r.key);

                                                        return (
                                                            <tr key={r.key || i}>
                                                                <td className="fw-bold text-center align-middle">{r.key}</td>

                                                                <td className="text-start align-middle">
                                                                    {r.key === "Order Type" ? (
                                                                        isEditing ? (
                                                                            <div className="d-flex flex-column gap-2">
                                                                                {orderTypeOptions.map((option) => {
                                                                                    const currentValues = Array.isArray(r.value) ? r.value : [];
                                                                                    const isSelected = currentValues.includes(option);

                                                                                    return (
                                                                                        <div key={option} className="form-check">
                                                                                            <input
                                                                                                className="form-check-input"
                                                                                                type="checkbox"
                                                                                                id={`order-type-${option}-${i}`}
                                                                                                checked={isSelected}
                                                                                                onChange={() => handleOrderTypeChange(option)}
                                                                                            />
                                                                                            <label
                                                                                                className="form-check-label"
                                                                                                htmlFor={`order-type-${option}-${i}`}
                                                                                            >
                                                                                                {option}
                                                                                            </label>
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        ) : (
                                                                            Array.isArray(r.value) && r.value.length > 0
                                                                                ? r.value.join(", ")
                                                                                : "N/A"
                                                                        )
                                                                    ) : r.key === "Business Plan" ? (
                                                                        isEditing && isEditableField ? (
                                                                            <select
                                                                                value={r.value || ""}
                                                                                onChange={(e) => handleBusinessPlanChange(e.target.value)}
                                                                                className="form-select w-75"
                                                                            >
                                                                                <option value="">Select Business Plan</option>
                                                                                {businessPlanOptions.map((option) => (
                                                                                    <option key={option} value={option}>
                                                                                        {option}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        ) : (
                                                                            r.value || "N/A"
                                                                        )
                                                                    ) : r.key === "Rating" ? (
                                                                        isEditing && isEditableField ? (
                                                                            <select
                                                                                value={r.value || 0}
                                                                                onChange={(e) => handleRatingChange(e.target.value)}
                                                                                className="form-select w-75"
                                                                            >
                                                                                {ratingOptions.map((option) => (
                                                                                    <option key={option.value} value={option.value}>
                                                                                        {option.label}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        ) : (
                                                                            <div>
                                                                                {r.value || 0} {r.value ? "★" : ""}
                                                                            </div>
                                                                        )
                                                                    ) : r.key === "Company Name" ? (
                                                                        <div>
                                                                            {isEditing && isEditableField ? (
                                                                                <input
                                                                                    type="text"
                                                                                    value={r.value || ""}
                                                                                    onChange={(e) => handleInputChange("value", e.target.value)}
                                                                                    className="form-control d-inline-block w-75 mb-2"
                                                                                />
                                                                            ) : (
                                                                                r.value || "N/A"
                                                                            )}
                                                                        </div>
                                                                    ) : r.key === "Bank Details" ? (
                                                                        ["holderName", "branchName", "accountNumber", "ifscCode"].map((field) => (
                                                                            <div key={field} className="mb-1">
                                                                                <strong>{field.replace(/([A-Z])/g, " $1")}:</strong>{" "}
                                                                                {isEditing && isEditableField ? (
                                                                                    <input
                                                                                        type="text"
                                                                                        value={r.value?.[field] || ""}
                                                                                        onChange={(e) => handleInputChange(field, e.target.value)}
                                                                                        className="form-control d-inline-block w-75 ms-2"
                                                                                    />
                                                                                ) : (
                                                                                    r.value?.[field] || "N/A"
                                                                                )}
                                                                            </div>
                                                                        ))
                                                                    ) : r.key === "Address" ? (
                                                                        ["doorNo", "street", "city", "state", "pincode", "landmark", "latitude", "longitude"].map((field) => (
                                                                            <div key={field} className="mb-1">
                                                                                <strong>{field.charAt(0).toUpperCase() + field.slice(1)}:</strong>{" "}
                                                                                {isEditing && isEditableField ? (
                                                                                    <input
                                                                                        type="text"
                                                                                        value={r.value?.[field] || ""}
                                                                                        onChange={(e) => handleInputChange(field, e.target.value)}
                                                                                        className="form-control d-inline-block w-75 ms-2"
                                                                                    />
                                                                                ) : (
                                                                                    r.value?.[field] || "N/A"
                                                                                )}
                                                                            </div>
                                                                        ))
                                                                    ) : r.key === "Trade License" ||
                                                                        r.key === "FSSI License" ||
                                                                        r.key === "Labour License" ? (
                                                                        <>
                                                                            <div className="mb-2">
                                                                                <strong>Start Date:</strong>{" "}
                                                                                {isEditing && isEditableField ? (
                                                                                    <input
                                                                                        type="date"
                                                                                        value={safeDateToISOString(r.value?.startDate)}
                                                                                        onChange={(e) => handleInputChange("startDate", e.target.value)}
                                                                                        className="form-control d-inline-block w-75 ms-2"
                                                                                    />
                                                                                ) : (
                                                                                    safeDateDisplay(r.value?.startDate)
                                                                                )}
                                                                            </div>
                                                                            <div>
                                                                                <strong>End Date:</strong>{" "}
                                                                                {isEditing && isEditableField ? (
                                                                                    <input
                                                                                        type="date"
                                                                                        value={safeDateToISOString(r.value?.endDate)}
                                                                                        onChange={(e) => handleInputChange("endDate", e.target.value)}
                                                                                        className="form-control d-inline-block w-75 ms-2"
                                                                                    />
                                                                                ) : (
                                                                                    safeDateDisplay(r.value?.endDate)
                                                                                )}
                                                                            </div>
                                                                        </>
                                                                    ) : r.key === "Trade License Number" ||
                                                                        r.key === "Labour License Number" ||
                                                                        r.key === "FSSAI License Number" ||
                                                                        r.key === "Owner Name" ||
                                                                        r.key === "Position" ||
                                                                        r.key === "BrandName" ||
                                                                        r.key === "Phone No" ||
                                                                        r.key === "Email ID" ||
                                                                        r.key === "Website" ||
                                                                        r.key === "GST No." ||
                                                                        r.key === "AADHAR CARD NO" ||
                                                                        r.key === "PAN CARD NO" ? (
                                                                        isEditing && isEditableField ? (
                                                                            <input
                                                                                type="text"
                                                                                value={r.value || ""}
                                                                                onChange={(e) => handleInputChange("value", e.target.value)}
                                                                                className="form-control d-inline-block w-75"
                                                                            />
                                                                        ) : (
                                                                            r.value || "N/A"
                                                                        )
                                                                    ) : r.key === "Register Documents" ? (
                                                                        "Registered Documents"
                                                                    ) : r.key === "Passbook" ? (
                                                                        "Passbook Image"
                                                                    ) : (
                                                                        r.value || "N/A"
                                                                    )}
                                                                </td>

                                                                <td className="text-center align-middle">
                                                                    {isEditing && isEditableField ? (
                                                                        <>
                                                                            {r.key === "Company Name" && (
                                                                                <input
                                                                                    type="file"
                                                                                    accept="image/*"
                                                                                    onChange={handleImageChange}
                                                                                    className="form-control mb-2"
                                                                                />
                                                                            )}
                                                                            {r.key === "Register Documents" && (
                                                                                <input
                                                                                    type="file"
                                                                                    multiple
                                                                                    accept="image/*"
                                                                                    onChange={handleImageChange}
                                                                                    className="form-control mb-2"
                                                                                />
                                                                            )}
                                                                            {r.key === "AADHAR CARD NO" && (
                                                                                <input
                                                                                    type="file"
                                                                                    multiple
                                                                                    accept="image/*"
                                                                                    onChange={handleImageChange}
                                                                                    className="form-control mb-2"
                                                                                />
                                                                            )}
                                                                            {r.key === "PAN CARD NO" && (
                                                                                <input
                                                                                    type="file"
                                                                                    accept="image/*"
                                                                                    onChange={handleImageChange}
                                                                                    className="form-control mb-2"
                                                                                />
                                                                            )}
                                                                            {r.key === "Trade License" && (
                                                                                <input
                                                                                    type="file"
                                                                                    accept="image/*"
                                                                                    onChange={handleImageChange}
                                                                                    className="form-control mb-2"
                                                                                />
                                                                            )}
                                                                            {r.key === "FSSI License" && (
                                                                                <input
                                                                                    type="file"
                                                                                    accept="image/*"
                                                                                    onChange={handleImageChange}
                                                                                    className="form-control mb-2"
                                                                                />
                                                                            )}
                                                                            {r.key === "Labour License" && (
                                                                                <input
                                                                                    type="file"
                                                                                    accept="image/*"
                                                                                    onChange={handleImageChange}
                                                                                    className="form-control mb-2"
                                                                                />
                                                                            )}
                                                                            {r.key === "Bank Details" && (
                                                                                <input
                                                                                    type="file"
                                                                                    accept="image/*"
                                                                                    onChange={handleImageChange}
                                                                                    className="form-control mb-2"
                                                                                />
                                                                            )}
                                                                            {r.key === "Passbook" && (
                                                                                <input
                                                                                    type="file"
                                                                                    accept="image/*"
                                                                                    onChange={handleImageChange}
                                                                                    className="form-control mb-2"
                                                                                />
                                                                            )}
                                                                            <ImageDisplay images={r.images} fieldName={r.key} />
                                                                        </>
                                                                    ) : (
                                                                        <ImageDisplay images={r.images} fieldName={r.key} />
                                                                    )}
                                                                </td>

                                                                <td className="text-center align-middle">
                                                                    {isEditing && isEditableField ? (
                                                                        <>
                                                                            <button
                                                                                className="btn btn-sm btn-success me-2"
                                                                                style={{ minWidth: 80 }}
                                                                                onClick={() => handleSaveRow(i)}
                                                                            >
                                                                                Save
                                                                            </button>
                                                                            <button
                                                                                className="btn btn-sm btn-secondary"
                                                                                style={{ minWidth: 80 }}
                                                                                onClick={() =>
                                                                                    setRows((prev) =>
                                                                                        prev.map((row, idx) =>
                                                                                            idx === i ? { ...row, isEditing: false } : row
                                                                                        )
                                                                                    )
                                                                                }
                                                                            >
                                                                                Cancel
                                                                            </button>
                                                                        </>
                                                                    ) : (
                                                                        <div style={{ display: "flex", gap: "10px" }}>
                                                                            {isFieldVerified(r.key, r.value, vendorDetails, r.images) ? (
                                                                                <button
                                                                                    className="btn btn-sm btn-success"
                                                                                    style={{ minWidth: 80 }}
                                                                                    disabled
                                                                                >
                                                                                    Verified ✓
                                                                                </button>
                                                                            ) : (
                                                                                <button
                                                                                    className="btn btn-sm btn-warning"
                                                                                    style={{ minWidth: 80 }}
                                                                                    onClick={() => handleVerifyy(i)}
                                                                                    disabled={!canVerifyField(r.key, r.value, vendorDetails, r.images) || !isEditableField}
                                                                                >
                                                                                    Verify
                                                                                </button>
                                                                            )}

                                                                            {isFieldRejected(r.key, vendorDetails) ? (
                                                                                <button
                                                                                    className="btn btn-sm btn-danger"
                                                                                    style={{ minWidth: 80 }}
                                                                                    disabled
                                                                                >
                                                                                    Rejected ✗
                                                                                </button>
                                                                            ) : (
                                                                                <button
                                                                                    className="btn btn-sm btn-secondary"
                                                                                    style={{ minWidth: 80 }}
                                                                                    onClick={() => handleReject(i)}
                                                                                    disabled={
                                                                                        r.key === "Rating" ? true :
                                                                                            !hasValidData({ key: r.key, value: r.value, images: r.images }, vendorDetails) ||
                                                                                            !isEditableField
                                                                                    }
                                                                                >
                                                                                    Reject
                                                                                </button>
                                                                            )}

                                                                            {/* Edit button - only show for editable fields */}
                                                                            {isEditableField && (
                                                                                <button
                                                                                    className="btn btn-sm btn-primary"
                                                                                    style={{ minWidth: 80 }}
                                                                                    onClick={() =>
                                                                                        setRows((prev) =>
                                                                                            prev.map((row, idx) =>
                                                                                                idx === i ? { ...row, isEditing: true } : row
                                                                                            )
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    Edit
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                <div style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    marginTop: "15px"
                                }}>
                                    {vendorDetails?.approvalStatus ? (
                                        <span
                                            style={{
                                                padding: "8px 20px",
                                                fontWeight: "600",
                                                borderRadius: "6px",
                                                backgroundColor: "#d1fae5",
                                                color: "#065f46"
                                            }}
                                        >
                                            Approved ✓
                                        </span>
                                    ) : (
                                        <button
                                            className="btn btn-success"
                                            style={{
                                                padding: "8px 20px",
                                                fontWeight: "600",
                                                borderRadius: "6px"
                                            }}
                                            onClick={handleApproveAll}
                                        >
                                            Approve
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const tableStyle = { width: "100%", borderCollapse: "collapse", marginTop: 5 };
const thTdStyle = { border: "1px solid #ddd", textAlign: "center", padding: "8px", verticalAlign: "middle" };
const thStyle = { ...thTdStyle, backgroundColor: "rgb(230 109 51)", color: "#fff", fontWeight: "bold", whiteSpace: "nowrap" };
const buttonStyle = { backgroundColor: "transparent", border: "none", cursor: "pointer" };

const detailThStyle = {
    ...thTdStyle,
    backgroundColor: "rgb(230 109 51)",
    color: "#fff",
    fontWeight: 600,
    padding: "0.75rem",
    verticalAlign: "middle",
    border: "1px solid #dee2e6",
    textAlign: "center"
};

export default FoodPaidClents;